<template>
  <div>
    <DocumentsAISummaryChatMessage
      v-if="shouldDisplaySummaryMessage"
      :document-summary="requestedSummary!"
      with-text-animation
    >
      <template #before-summary>
        <DocumentsAISummaryOcrProcessInfosMessages
          :status="status"
          @launchConversion="launchConversion"
        />
      </template>
    </DocumentsAISummaryChatMessage>

    <DocumentsAISummaryMessageFromAI
      v-else
      :message-date="requestDate"
      :isSummaryRequestPending="false"
      shouldDisplayErrorMessage
      :shouldDisplaySummaryMessage="shouldDisplaySummaryMessage"
    >
      <template #before-summary>
        <DocumentsAISummaryOcrProcessInfosMessages
          :status="status"
          @launchConversion="launchConversion"
        />
      </template>
    </DocumentsAISummaryMessageFromAI>
  </div>
</template>

<script lang="ts">
import dayjs from "dayjs";
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import { OcrNotifications } from "@/common/utils/notifications";
import DocumentsAISummaryChatMessage from "@/project/documents/ai-summary/DocumentsAISummaryChatMessage.vue";
import DocumentsAISummaryMessageFromAI from "@/project/documents/ai-summary/DocumentsAISummaryMessageFromAI.vue";
import DocumentsAISummaryOcrProcessInfosMessages
  from "@/project/documents/ai-summary/DocumentsAISummaryOcrProcessInfosMessages.vue";
import aiSummariesService from "@/services/ai-summaries.service";
import documentsService from "@/services/documents.service";
import { realtimeService } from "@/services/realtime.service";

export type DocumentAISummaryOcrStatus = 'available' | 'ocrPending' | 'summaryPending' | 'errorOnOcr' | 'errorOnSummary' | 'finished'

type OcrNotification = {
  file: number,
}

export default defineComponent({
  name: 'DocumentsAISummaryOcrProcess',
  components: {
    DocumentsAISummaryOcrProcessInfosMessages,
    DocumentsAISummaryChatMessage,
    DocumentsAISummaryMessageFromAI,
  },
  emits: ['update-status'],
  data() {
    return {
      requestDate: dayjs().toISOString(),
      requestedSummary: null,
    }
  },
  props: {
    status: {
      type: String as PropType<DocumentAISummaryOcrStatus>,
      required: true,
    },
  },
  computed: {
    ...mapState('user', ['profile']),
    ...mapState('documentsAISummary', ['documentToSummarize']),
    ...mapGetters('room', ['roomMnemo']),
    shouldDisplaySummaryMessage() {
      return this.status === 'finished' && !!this.requestedSummary
    },
  },
  methods: {
    async launchConversion() {
      this.updateStatus('ocrPending')
      try {
        documentsService.postFileOcr(this.roomMnemo, this.documentToSummarize.id)
        this.startListeningToOcrEvents()
      } catch (error) {
        this.updateStatus('errorOnOcr')
      }
    },
    startListeningToOcrEvents() {
      realtimeService.on([OcrNotifications.OCR_FINISHED, OcrNotifications.OCR_ERROR], this.handleOcrEvent)
    },
    stopListeningToOcrEvents() {
      realtimeService.off([OcrNotifications.OCR_FINISHED, OcrNotifications.OCR_ERROR], this.handleOcrEvent)
    },
    handleOcrEvent (event: string, data: NotificationData) {
      if (this.status !== 'ocrPending' || (data as unknown as OcrNotification).file !== this.documentToSummarize.id) {
        return
      }

      if (event === OcrNotifications.OCR_FINISHED) {
        this.requestSummary()
      } else if (event === OcrNotifications.OCR_ERROR) {
        this.updateStatus('errorOnOcr')
      }
    },
    async requestSummary() {
      this.updateStatus('summaryPending')
      try {
        const { data } = await aiSummariesService.requestDocumentAISummary(
          this.roomMnemo,
          this.documentToSummarize.id,
        )
        this.requestedSummary = data
        this.updateStatus('finished')
      } catch (error: any) {
        console.error(error)
        this.updateStatus('errorOnSummary')
      }
    },
    updateStatus(newStatus: DocumentAISummaryOcrStatus) {
      this.$emit('update-status', newStatus)
    },
  },
  mounted() {
    if (this.status === 'ocrPending') {
      this.startListeningToOcrEvents()
    }
  },
  unmounted() {
    this.stopListeningToOcrEvents()
  },
})
</script>
